import React from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import {
  CircularLoader,
  PaperclipIcon,
  TextareaAutosize as TextareaAutosizeBase,
} from '@blade/ui-kit'
import { Close5Icon, PaperPlane2Icon, TrashIcon } from '@root/shared/ui-kit'

import type { DropEvent, DropzoneOptions, FileRejection } from 'react-dropzone'

const ATTACHMENT_FILE_SIZE = 5 * 1024 * 1024

const ATTACHMENT_DROPZONE_OPTIONS: DropzoneOptions = {
  maxSize: ATTACHMENT_FILE_SIZE,
  multiple: false,
  accept: {
    'image/*': ['.jpeg', '.png'],
  },
}

type Props = {
  isLoading?: boolean
  disabled?: boolean
  disabledSend?: boolean
  minRows?: number
  text?: string
  imageAttached?: string

  onChange: (value: string) => void
  onMessageSend: (text: string) => void
  onClose: () => void
  onImageAttachedChange?: (image: string | undefined) => void
  withClose?: boolean
  placeholder?: string
}

// Temporary disabled
const ATTACH_DISABLED = true

/**
 * TODO: Move to shared/ui-kit
 */
export const CommentMessage = ({
  disabled = false,
  isLoading = false,
  disabledSend = false,
  minRows = 1,
  text,
  imageAttached,

  onChange,
  onMessageSend,
  onClose,
  onImageAttachedChange,
  withClose = false,
  placeholder,
  ...props
}: Props) => {
  const isDisabled = isLoading || disabled
  const { t } = useTranslation()
  const formRef = React.useRef<HTMLFormElement>(null)

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...ATTACHMENT_DROPZONE_OPTIONS,
    // disabled: isDisabled,
    disabled: ATTACH_DISABLED,
    onDrop: (
      acceptedFiles: Array<File>,
      _fileRejections: Array<FileRejection>,
      _event: DropEvent,
    ) => {
      for (const acceptedFile of acceptedFiles) {
        const img = new Image()

        img.onload = () => {
          const reader = new FileReader()
          reader.readAsDataURL(acceptedFile)
          reader.onload = () => {
            onImageAttachedChange?.(reader.result as string)
          }
        }

        img.src = URL.createObjectURL(acceptedFile)
      }
    },
  })

  const handleSendAndClear = () => {
    if (!text) {
      return
    }

    onMessageSend(text)
    onChange('')
  }

  const renderAttach = () => {
    if (!imageAttached) {
      return (
        <Attach {...getRootProps()}>
          <input {...getInputProps()} />
          <ButtonAttach size={16} aria-disabled={ATTACH_DISABLED} />
        </Attach>
      )
    }

    return (
      <Attach>
        <AttachedImage $background={imageAttached}>
          <AttachRemove onClick={() => onImageAttachedChange?.(undefined)}>
            <TrashIcon size={12} color="var(--background-11)" />
          </AttachRemove>
        </AttachedImage>
      </Attach>
    )
  }

  return (
    <Root {...props} $withClose={withClose}>
      <Message
        onSubmit={(e) => {
          e.preventDefault()
          handleSendAndClear()
        }}
        $active={isDragActive}
      >
        {renderAttach()}
        <TextareaAutosize
          disabled={isDisabled}
          minRows={minRows}
          value={text}
          placeholder={placeholder ?? t('comment.message.placeholder')}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              const form = formRef.current
              if (!form) {
                return
              }

              if (typeof form.requestSubmit === 'function') {
                form.requestSubmit()
              } else {
                form.submit()
              }
            }
          }}
        />
        <FixedHeight>
          <Separator />
          {isLoading ? (
            <CircularLoader size={16} />
          ) : (
            <ButtonSend
              aria-busy={!!text}
              disabled={disabled || disabledSend || !text}
            >
              <ButtonSendIcon size={16} />
            </ButtonSend>
          )}
        </FixedHeight>
      </Message>
      {withClose ? (
        <FixedHeight>
          <ButtonClose onClick={onClose} disabled={isDisabled}>
            <Close5Icon size={14} />
          </ButtonClose>
        </FixedHeight>
      ) : undefined}
    </Root>
  )
}

const Root = styled.div<{ $withClose?: boolean }>`
  display: grid;
  grid-template-columns: 1fr auto;
  justify-content: center;
  align-content: center;
  align-items: center;

  align-items: end;
  gap: ${({ $withClose }) => ($withClose ? '8px' : '0')};
`

const Message = styled.form<{ $active: boolean }>`
  border: 1px dashed
    ${(props) => (props.$active ? 'var(--background-9)' : 'transparent')};

  background: var(--comment-message-background, var(--background-2));

  padding-inline-end: 10px;
  border-radius: 10px;
  min-height: 40px;
  gap: 8px;

  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: end;
`

const Attach = styled.div`
  height: 40px;
  width: 32px;

  display: grid;
  align-items: center;
  grid-auto-flow: column;
  position: relative;
  cursor: default;

  position: relative;
`

const AttachedImage = styled.div<{ $background: string }>`
  background: ${(props) => `url(${props.$background})`};
  border: 1px solid var(--backgroundA-3);

  height: 26px;
  width: 26px;
  border-radius: 8px;
  margin-inline-start: 6px;

  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;

  position: relative;
  overflow: hidden;
`

const AttachRemove = styled.div`
  background: var(--appearance-1);

  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;

  display: grid;
  justify-content: center;
  align-items: center;

  ${Attach}:hover & {
    opacity: 0.5;
  }
`

const FixedHeight = styled.div`
  height: 40px;

  display: grid;
  align-items: center;
  grid-auto-flow: column;
`

const ButtonClose = styled.button`
  color: var(--text-6);
  border: 1px solid var(--backgroundA-4);
  background: var(--background-1);
  border-radius: 50%;

  width: 26px;
  height: 26px;
  margin-bottom: 3px;
  display: grid;
  align-items: center;
  justify-items: center;

  transition: all 0.1s ease-in-out;
  cursor: pointer;

  &:hover:not([disabled]) {
    color: var(--text-11);
    border: 1px solid var(--backgroundA-8);
  }
`

const ButtonAttach = styled(PaperclipIcon)`
  color: var(--text-9);

  margin-inline-start: 10px;
  transition: color 0.1s ease-in-out;
  margin-right: 10px;
  cursor: not-allowed;

  &:hover:not([aria-disabled='true']) {
    cursor: pointer;
    color: var(--text-12);
  }
`

const ButtonSend = styled.button`
  all: unset;
  color: var(--textA-9);
  cursor: pointer;
  display: grid;

  transition: color 0.1s ease-in-out;

  &[aria-busy='true'] {
    cursor: pointer;
    color: var(--primary-9);
  }
  &:disabled {
    cursor: default;
    color: var(--textA-6);
  }
`
const ButtonSendIcon = styled(PaperPlane2Icon)`
  transition: color 0.1s ease-in-out;
`

const TextareaAutosize = styled(TextareaAutosizeBase)`
  caret-color: var(--primary-9);
  color: var(--text-11);

  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  border: none;
  resize: none;

  width: 100%;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  line-height: 16px;
  padding-block: 12px;

  &::placeholder {
    color: var(--text-8);
    font-weight: 400;
  }
  &:disabled {
    color: var(--text-8);
  }
`

const Separator = styled.span`
  background: var(--backgroundA-3);

  height: 26px;
  width: 1px;

  border-radius: 2px;
  margin-inline-end: 8px;
`
